import { useEffect, useRef } from "react";
import { mediaUrl } from "@/lib/api";
import { X, ChevronLeft, ChevronRight, Heart, Check, Download } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export function Lightbox({
  photos, index, onClose, setIndex,
  selectable = false, favoritable = false, downloadable = false,
  onToggleSelect, onToggleFavorite, watermark = false, onDownload,
}) {
  const touchX = useRef(null);
  const open = index !== null && index >= 0;
  const photo = open ? photos[index] : null;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIndex((i) => Math.min(i + 1, photos.length - 1));
      if (e.key === "ArrowLeft") setIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, photos.length, onClose, setIndex]);

  if (!open || !photo) return null;

  const download = () => {
    if (onDownload) { onDownload(photo); return; }
    const a = document.createElement("a");
    a.href = mediaUrl(photo.url);
    a.download = photo.original_filename || "pmr-photo.jpg";
    a.target = "_blank";
    a.click();
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center"
        data-testid="lightbox"
        onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
        onTouchEnd={(e) => {
          if (touchX.current === null) return;
          const dx = e.changedTouches[0].clientX - touchX.current;
          if (dx < -50) setIndex((i) => Math.min(i + 1, photos.length - 1));
          if (dx > 50) setIndex((i) => Math.max(i - 1, 0));
          touchX.current = null;
        }}>
        <button data-testid="lightbox-close" onClick={onClose} className="absolute top-5 right-5 w-11 h-11 rounded-full glass flex items-center justify-center text-white hover:bg-white/10 z-10">
          <X className="w-5 h-5" />
        </button>

        <div className="absolute top-5 left-5 glass rounded-full px-4 py-2 text-sm text-white z-10">{index + 1} / {photos.length}</div>

        {index > 0 && (
          <button data-testid="lightbox-prev" onClick={() => setIndex((i) => i - 1)} className="absolute left-3 sm:left-8 w-12 h-12 rounded-full glass flex items-center justify-center text-white hover:bg-white/10 z-10">
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}
        {index < photos.length - 1 && (
          <button data-testid="lightbox-next" onClick={() => setIndex((i) => i + 1)} className="absolute right-3 sm:right-8 w-12 h-12 rounded-full glass flex items-center justify-center text-white hover:bg-white/10 z-10">
            <ChevronRight className="w-6 h-6" />
          </button>
        )}

        <motion.div key={photo.id} initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}
          className="relative max-w-[90vw] max-h-[82vh]">
          <img src={mediaUrl(photo.url)} alt="" className="max-w-[90vw] max-h-[82vh] object-contain rounded-lg" />
          {watermark && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-15">
              <span className="font-serif text-white text-4xl rotate-[-20deg] tracking-widest">PMR Events</span>
            </div>
          )}
        </motion.div>

        <div className="absolute bottom-6 inset-x-0 flex items-center justify-center gap-3 z-10">
          {favoritable && (
            <button data-testid="lightbox-favorite" onClick={() => onToggleFavorite?.(photo)}
              className="flex items-center gap-2 glass rounded-full px-5 py-2.5 text-white hover:bg-white/10">
              <Heart className={`w-4 h-4 ${photo.favorite ? "text-gold fill-gold" : ""}`} /> Favourite
            </button>
          )}
          {selectable && (
            <button data-testid="lightbox-select" onClick={() => onToggleSelect?.(photo)}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 font-medium transition-colors ${photo.selected ? "bg-gold text-black" : "glass text-white hover:bg-white/10"}`}>
              <Check className="w-4 h-4" strokeWidth={3} /> {photo.selected ? "Selected" : "Select"}
            </button>
          )}
          {downloadable && (
            <button data-testid="lightbox-download" onClick={download} className="flex items-center gap-2 glass rounded-full px-5 py-2.5 text-white hover:bg-white/10">
              <Download className="w-4 h-4" /> Download
            </button>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
